import { objectKeys } from "./general";
import { MAX_REQUEST_SIZE } from "~/assets/constants";

const OUTPUT_QUALITY = 0.5;
const CONVERSION_FILE_EXT = 'jpeg';
const CONVERSION_MIME = 'image/' + CONVERSION_FILE_EXT;
const BASE_64_SIGNATURES = {
  JVBERi0: "application/pdf",
  R0lGODdh: "image/gif",
  R0lGODlh: "image/gif",
  iVBORw0KGgo: "image/png",
  "/9j/": "image/jpg"
} as const;
export const ALLOWED_FILE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'heic'];
export const IMAGE_MIME_TYPES = { image: 'image/*', pdf: 'application/pdf' };
export const MAX_FILE_SIZE = 10;

export function getFileExtension(file: File) {
  return file.name.split('.').pop()!.toLowerCase();
}

export function isPDF(file: File) {
  return file.type == IMAGE_MIME_TYPES.pdf;
}

export function convertToBase64(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result!.toString());
    reader.onerror = error => reject(error);
  });
}

export function detectMimeType(b64: string) {
  const base64 = cleanBase64(b64);
  for (const s of objectKeys(BASE_64_SIGNATURES)) {
    if (base64.indexOf(s) === 0) {
      return BASE_64_SIGNATURES[s];
    }
  }
}

export function base64ToBuffer(base64: string) {
  return Buffer.from(cleanBase64(base64), 'base64');
}

export async function compressFile(file: File) {
  if (!ALLOWED_FILE_EXTENSIONS.includes(getFileExtension(file))) {
    throw new Error(`Unable to compress file of type ${file.type}`);
  }
  if (isPDF(file)) {
    if (isFileSizeOk(file)) return file;
    const { convertPdfToImage } = await import('./pdf');
    file = await convertPdfToImage(file);
  }
  return compressImage(file, CONVERSION_MIME, OUTPUT_QUALITY);
}

function compressImage(file: File, type: string, quality: number): Promise<File> {
  return new Promise(resolve => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(blob => {
        resolve(new File([blob!], file.name, { type }));
      }, type, quality);
    }
  });
}

function cleanBase64(base64: string) {
  const arr = base64.split(',');
  return arr[1] || arr[0];
}

function isFileSizeOk(file: File) {
  return file.size <= MAX_REQUEST_SIZE;
}